import {
  getLocalDateString,
  normalizeDateString,
  parseLocalDateString,
} from './dateUtils';
import { SPECIALIZATION_TO_SERVICE } from './serviceTypeUtils';

export const IE_LEAD_MONTHS = 1;
export const IE_PENALTY_PER_DAY = 100;
export const IE_PENALTY_PAYROLL_EFFECTIVE_DATE = '2026-08-27';
export const IE_LEGACY_SUBMITTED_CUTOFF_DATE = '2026-08-31';

export const IE_STATUSES = {
  CONDUCTED: 'conducted',
  THERAPIST_NOTIFIED: 'therapist_notified',
  SUBMITTED: 'submitted',
};

export const IE_STATUS_LABELS = {
  conducted: 'Conducted',
  therapist_notified: 'Therapist Notified',
  submitted: 'Submitted',
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function toLocalMidnight(value) {
  if (!value) {
    return null;
  }

  if (typeof value === 'string' && DATE_ONLY_PATTERN.test(value)) {
    return parseLocalDateString(value);
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(dateStr, days) {
  const date = toLocalMidnight(dateStr);
  if (!date) {
    return null;
  }
  date.setDate(date.getDate() + days);
  return getLocalDateString(date);
}

/**
 * Add calendar months to a YYYY-MM-DD date (handles month-end clamping).
 */
export function addMonthsToDate(dateStr, months) {
  const date = toLocalMidnight(dateStr);
  if (!date) {
    return null;
  }

  const day = date.getDate();
  date.setDate(1);
  date.setMonth(date.getMonth() + months);
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  date.setDate(Math.min(day, lastDay));
  return getLocalDateString(date);
}

export function computeIeDueDate(conductedAt) {
  return addMonthsToDate(conductedAt, IE_LEAD_MONTHS);
}

function daysBetweenInclusive(startStr, endStr) {
  const start = toLocalMidnight(startStr);
  const end = toLocalMidnight(endStr);
  if (!start || !end || end < start) {
    return 0;
  }

  const diffMs = end.getTime() - start.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
}

/**
 * First calendar day the IE report is considered late.
 */
export function getFirstLateDate(dueDate) {
  return addDays(dueDate, 1);
}

/**
 * Full penalty amount (tab display). Counts every day late through referenceDate.
 */
export function computeIePenalty(dueDate, referenceDate = getLocalDateString(new Date())) {
  const firstLate = getFirstLateDate(dueDate);
  if (!firstLate) {
    return 0;
  }

  const ref = normalizeDateString(referenceDate);
  if (!ref || ref < firstLate) {
    return 0;
  }

  const daysLate = daysBetweenInclusive(firstLate, ref);
  return daysLate * IE_PENALTY_PER_DAY;
}

/**
 * Penalty days within a pay period, clamped to payroll effective date.
 */
export function getPayrollPenaltyDaysInPeriod(
  dueDate,
  periodStart,
  periodEnd,
  asOfDate = getLocalDateString(new Date()),
  payrollEffectiveDate = IE_PENALTY_PAYROLL_EFFECTIVE_DATE
) {
  const periodEndNorm = normalizeDateString(periodEnd);
  if (!periodEndNorm || periodEndNorm < payrollEffectiveDate) {
    return 0;
  }

  const firstLate = getFirstLateDate(dueDate);
  if (!firstLate) {
    return 0;
  }

  const penaltyStart = firstLate > payrollEffectiveDate ? firstLate : payrollEffectiveDate;
  const ref = normalizeDateString(asOfDate);
  const periodStartNorm = normalizeDateString(periodStart);
  const asOf = ref && periodEndNorm && ref < periodEndNorm ? ref : periodEndNorm;

  const rangeStart =
    penaltyStart > periodStartNorm ? penaltyStart : periodStartNorm;
  const rangeEnd = asOf;

  if (!rangeStart || !rangeEnd || rangeEnd < rangeStart) {
    return 0;
  }

  return daysBetweenInclusive(rangeStart, rangeEnd);
}

export function computePayrollIePenaltyInPeriod(
  dueDate,
  periodStart,
  periodEnd,
  asOfDate,
  payrollEffectiveDate
) {
  const days = getPayrollPenaltyDaysInPeriod(
    dueDate,
    periodStart,
    periodEnd,
    asOfDate,
    payrollEffectiveDate
  );
  return days * IE_PENALTY_PER_DAY;
}

export function dueDateToSubmittedTimestamp(dueDate) {
  const date = parseLocalDateString(normalizeDateString(dueDate));
  date.setHours(12, 0, 0, 0);
  return date.toISOString();
}

export function shouldLegacyAutoSubmit(row, cutoffDate = IE_LEGACY_SUBMITTED_CUTOFF_DATE) {
  if (row?.submitted_at || row?.status === IE_STATUSES.SUBMITTED) {
    return false;
  }

  const due = normalizeDateString(row?.due_date);
  return Boolean(due && due <= cutoffDate);
}

export function resolveServiceTypeFromAttendance(attendance, therapist) {
  if (attendance?.billing_service_type) {
    return attendance.billing_service_type;
  }

  const spec = therapist?.specialization;
  return SPECIALIZATION_TO_SERVICE[spec] || 'OT';
}

export async function upsertInitialEvaluationFromAttendance(
  supabaseClient,
  { attendanceId, clientId, therapistId, serviceType, conductedAt }
) {
  const conducted = normalizeDateString(conductedAt);
  if (!attendanceId || !clientId || !therapistId || !serviceType || !conducted) {
    throw new Error('Missing required fields for initial evaluation upsert');
  }

  const dueDate = computeIeDueDate(conducted);
  const now = new Date().toISOString();

  const { data: existing } = await supabaseClient
    .from('initial_evaluation_submissions')
    .select('id, status')
    .eq('session_attendance_id', attendanceId)
    .maybeSingle();

  if (existing) {
    const { error } = await supabaseClient
      .from('initial_evaluation_submissions')
      .update({
        client_id: clientId,
        therapist_id: therapistId,
        service_type: serviceType,
        conducted_at: conducted,
        due_date: dueDate,
        updated_at: now,
      })
      .eq('id', existing.id);

    if (error) {
      throw error;
    }
    return existing.id;
  }

  const { data, error } = await supabaseClient
    .from('initial_evaluation_submissions')
    .insert([
      {
        session_attendance_id: attendanceId,
        client_id: clientId,
        therapist_id: therapistId,
        service_type: serviceType,
        conducted_at: conducted,
        due_date: dueDate,
        status: IE_STATUSES.CONDUCTED,
        updated_at: now,
      },
    ])
    .select('id')
    .single();

  if (error) {
    throw error;
  }

  return data.id;
}

function pickMatchingDocument(documents, clientId, conductedAt) {
  const conducted = normalizeDateString(conductedAt);
  const matches = (documents || [])
    .filter(
      (doc) =>
        doc.client_id === clientId &&
        doc.document_type === 'Initial Evaluation' &&
        normalizeDateString(doc.created_at) >= conducted
    )
    .sort(
      (a, b) =>
        normalizeDateString(a.created_at).localeCompare(normalizeDateString(b.created_at))
    );

  return matches[0] || null;
}

const INSERT_CHUNK_SIZE = 100;
const IN_FILTER_CHUNK_SIZE = 100;

async function fetchDocumentsByClientIds(supabaseClient, clientIds) {
  const documents = [];

  for (let i = 0; i < clientIds.length; i += IN_FILTER_CHUNK_SIZE) {
    const chunk = clientIds.slice(i, i + IN_FILTER_CHUNK_SIZE);
    const { data, error } = await supabaseClient
      .from('client_documents')
      .select('id, client_id, document_type, created_at')
      .in('client_id', chunk)
      .eq('document_type', 'Initial Evaluation');

    if (error) {
      throw error;
    }

    documents.push(...(data || []));
  }

  return documents;
}

/**
 * Backfill rows from session_attendance and auto-match submitted documents.
 */
export async function syncInitialEvaluationSubmissions(supabaseClient) {
  const { data: attendanceRows, error: attendanceError } = await supabaseClient
    .from('session_attendance')
    .select('id, client_id, therapist_id, attendance_date, billing_service_type')
    .eq('session_type', 'IE')
    .eq('status', 'present');

  if (attendanceError) {
    throw attendanceError;
  }

  if (attendanceRows?.length) {
    const { data: existingRows, error: existingError } = await supabaseClient
      .from('initial_evaluation_submissions')
      .select('session_attendance_id');

    if (existingError) {
      throw existingError;
    }

    const existingIds = new Set(
      (existingRows || []).map((row) => row.session_attendance_id)
    );
    const now = new Date().toISOString();

    const newRows = attendanceRows
      .filter((row) => !existingIds.has(row.id))
      .map((row) => {
        const conducted = normalizeDateString(row.attendance_date);
        return {
          session_attendance_id: row.id,
          client_id: row.client_id,
          therapist_id: row.therapist_id,
          service_type: resolveServiceTypeFromAttendance(row, null),
          conducted_at: conducted,
          due_date: computeIeDueDate(conducted),
          status: IE_STATUSES.CONDUCTED,
          updated_at: now,
        };
      });

    for (let i = 0; i < newRows.length; i += INSERT_CHUNK_SIZE) {
      const chunk = newRows.slice(i, i + INSERT_CHUNK_SIZE);
      const { error: insertError } = await supabaseClient
        .from('initial_evaluation_submissions')
        .insert(chunk);

      if (insertError) {
        throw insertError;
      }
    }
  }

  const { data: pendingRows, error: pendingError } = await supabaseClient
    .from('initial_evaluation_submissions')
    .select('id, client_id, conducted_at, status')
    .in('status', [IE_STATUSES.CONDUCTED, IE_STATUSES.THERAPIST_NOTIFIED]);

  if (pendingError) {
    throw pendingError;
  }

  const now = new Date().toISOString();

  if (pendingRows?.length) {
    const clientIds = [...new Set(pendingRows.map((r) => r.client_id))];
    const documents = await fetchDocumentsByClientIds(supabaseClient, clientIds);

    const submissionUpdates = pendingRows
      .map((row) => {
        const match = pickMatchingDocument(documents, row.client_id, row.conducted_at);
        if (!match) {
          return null;
        }

        return supabaseClient
          .from('initial_evaluation_submissions')
          .update({
            status: IE_STATUSES.SUBMITTED,
            submitted_at: match.created_at,
            client_document_id: match.id,
            updated_at: now,
          })
          .eq('id', row.id);
      })
      .filter(Boolean);

    await Promise.all(submissionUpdates);
  }

  const { data: legacyRows, error: legacyError } = await supabaseClient
    .from('initial_evaluation_submissions')
    .select('id, due_date, submitted_at, status')
    .lte('due_date', IE_LEGACY_SUBMITTED_CUTOFF_DATE)
    .in('status', [IE_STATUSES.CONDUCTED, IE_STATUSES.THERAPIST_NOTIFIED]);

  if (legacyError) {
    throw legacyError;
  }

  const legacyUpdates = (legacyRows || [])
    .filter((row) => shouldLegacyAutoSubmit(row))
    .map((row) =>
      supabaseClient
        .from('initial_evaluation_submissions')
        .update({
          status: IE_STATUSES.SUBMITTED,
          submitted_at: dueDateToSubmittedTimestamp(row.due_date),
          updated_at: now,
        })
        .eq('id', row.id)
    );

  await Promise.all(legacyUpdates);
}
